import { useState } from 'react';
import { isLevelUnit, indexInputToStored, storedToIndexInput, indexInputLabel } from '@harvus/shared';
import { B, radius } from '../constants/brand';
import { fmt } from '../utils/format';
import { api } from '../api/client';
import { useApi } from '../hooks/useApi';
import { Btn, Input, Modal, ModalFooter, Spinner, ErrorMsg, Card, Table, useToast, Badge, InfoNote, PageHeader, EmptyState, Icon, clickable } from '../components/ui';

// Tiny SVG sparkline — renders an array of rate values as a path
const Sparkline = ({ values, width = 80, height = 28, color }) => {
  if (!values || values.length < 2) return null;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 0.0001;
  const pts = values.map((v, i) => {
    const x = (i / (values.length - 1)) * width;
    const y = height - ((v - min) / range) * height;
    return `${x},${y}`;
  }).join(" ");
  return (
    <svg width={width} height={height} style={{ display: "block" }}>
      <polyline points={pts} fill="none" stroke={color || B.navyDk} strokeWidth={1.5} strokeLinejoin="round" />
    </svg>
  );
};

export const RateIndexes = () => {
  const { toast } = useToast();
  const { data: rates, loading, error, reload } = useApi(() => api.get("rate-indexes"), []);
  const [editing, setEditing]       = useState(null);
  const [newRate, setNewRate]       = useState("");
  const [saving, setSaving]         = useState(false);
  const [historyIndex, setHistoryIndex] = useState(null); // { indexID, indexName }
  const [history, setHistory]       = useState([]);
  const [histLoading, setHistLoading] = useState(false);
  const [syncing, setSyncing]       = useState(false);

  const editingRow = (rates || []).find(r => r.indexID === editing);

  const saveRate = async () => {
    setSaving(true);
    try {
      // A percentage is typed as "5.33" and stored as 0.0533; an index level is
      // typed and stored as points. The unconditional /100 here is exactly what
      // would have mangled the S&P 500.
      await api.put(`rate-indexes/${editing}`, {
        currentRate: indexInputToStored(newRate, editingRow?.unit),
      });
      setEditing(null); setNewRate(""); reload();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const openHistory = async (row) => {
    setHistoryIndex({ indexID: row.indexID, indexName: row.indexName, unit: row.unit });
    setHistory([]);
    setHistLoading(true);
    try {
      const rows = await api.get(`rate-indexes/${row.indexID}/history?days=365`); // 1 year
      // Keep only rows where rate changed from previous
      const changes = rows.filter((r, i) => i === 0 || r.rate !== rows[i - 1].rate);
      setHistory(changes);
    } catch { setHistory([]); }
    finally { setHistLoading(false); }
  };

  const triggerSync = async () => {
    setSyncing(true);
    try {
      await api.post("rate-index-sync", {});
      reload();
    } catch (e) { toast.error(e.message); }
    finally { setSyncing(false); }
  };

  if (loading) return <Spinner />;
  if (error)   return <ErrorMsg msg={error} onRetry={reload} />;

  return (
    <div>
      <PageHeader
        icon="rates"
        title="Rate Indexes"
        subtitle="Auto-pulled daily at 06:00 Central from FRED (rows marked Daily). Click any value to view history, or update manually."
        actions={
          <Btn variant="secondary" onClick={triggerSync} disabled={syncing}>
            <Icon name="sync" size={14} /> {syncing ? "Syncing…" : "Sync FRED Now"}
          </Btn>
        }
      />

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(190px,1fr))", gap: 16, marginBottom: 28 }}>
        {(rates || []).filter(r => r.indexName?.toLowerCase() !== 'fixed rate' && r.indexName?.toLowerCase() !== 'fixed').map(r => (
          <div key={r.indexID} {...clickable(() => openHistory(r))}
            style={{ background: B.navy, borderRadius: radius.xl, padding: "16px 18px", cursor: "pointer", transition: "opacity 0.15s" }}
            onMouseEnter={e => e.currentTarget.style.opacity = "0.85"}
            onMouseLeave={e => e.currentTarget.style.opacity = "1"}>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.6)", fontWeight: 600, letterSpacing: 0.5, marginBottom: 4 }}>{r.indexName}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: "#fff", marginBottom: 4 }}>{fmt.indexValue(r.currentRate, r.unit)}</div>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.5)" }}>Updated {fmt.date(r.lastUpdatedUTC)}</div>
          </div>
        ))}
      </div>

      <Card style={{ padding: 0 }}>
        <Table
          columns={[
            { key: "indexName",   label: "Index",        render: v => <span style={{ fontWeight: 700 }}>{v}</span> },
            { key: "displayName", label: "Description" },
            { key: "currentRate", label: "Current",
              render: (v, row) => (
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ fontWeight: 700, fontSize: 15, color: B.navy, cursor: "pointer", textDecoration: "underline dotted" }}
                    onClick={() => openHistory(row)}>
                    {fmt.indexValue(v, row.unit)}
                  </span>
                  <Sparkline values={(row._sparkValues || [])} color={B.tealDk} />
                </div>
              )
            },
            { key: "lastUpdatedUTC", label: "Last Updated", render: v => fmt.date(v) },
            { key: "autoPull",       label: "Auto-Pull",    render: v => <Badge color={v ? B.green : B.textLt}>{v ? "Daily" : "Manual"}</Badge> },
            { key: "actions", label: "", render: (_, row) => (
              <div style={{ display: "flex", gap: 6 }}>
                <Btn size="sm" variant="ghost" onClick={() => openHistory(row)}>History</Btn>
                <Btn size="sm" variant="ghost" onClick={() => {
                  setEditing(row.indexID);
                  setNewRate(storedToIndexInput(row.currentRate, row.unit).toFixed(isLevelUnit(row.unit) ? 2 : 4));
                }}>Update</Btn>
              </div>
            )},
          ]}
          data={(rates || []).filter(r => r.indexName?.toLowerCase() !== 'fixed rate' && r.indexName?.toLowerCase() !== 'fixed')}
        />
      </Card>

      {/* Manual rate update modal */}
      {editing && (
        <Modal title={`Update ${isLevelUnit(editingRow?.unit) ? "Level" : "Rate"} — ${editingRow?.indexName}`} onClose={() => { setEditing(null); setNewRate(""); }}>
          <div style={{ fontSize: 13, color: B.textMd, marginBottom: 16 }}>
            Current: <strong>{fmt.indexValue(editingRow?.currentRate, editingRow?.unit)}</strong>
          </div>
          <Input label={indexInputLabel(editingRow?.unit)} type="number" value={newRate} onChange={setNewRate}
                 placeholder={isLevelUnit(editingRow?.unit) ? "e.g. 6450.16" : "e.g. 5.33"} />
          {/* An index level prices no loans — only a rate index feeds the
              variable-rate payment schedule, so only it carries the warning. */}
          {!isLevelUnit(editingRow?.unit) && (
            <InfoNote>Saving will trigger recalculation of all outstanding Variable-rate loan payments that reference this index.</InfoNote>
          )}
          <ModalFooter>
            <Btn variant="ghost" onClick={() => { setEditing(null); setNewRate(""); }}>Cancel</Btn>
            <Btn disabled={!newRate || saving} onClick={saveRate}>{saving ? "Saving…" : "Save Rate"}</Btn>
          </ModalFooter>
        </Modal>
      )}

      {/* History modal */}
      {historyIndex && (
        <Modal title={`${historyIndex.indexName} — History`} onClose={() => { setHistoryIndex(null); setHistory([]); }} width={640}>
          {histLoading && <Spinner />}
          {!histLoading && history.length === 0 && (
            <EmptyState icon="rates" title="No history yet" message="No historical data yet. Use “Sync FRED Now” to pull data." />
          )}
          {!histLoading && history.length > 1 && (
            <div style={{ marginBottom: 20 }}>
              <svg width="100%" height="80" viewBox={`0 0 ${history.length} 80`} preserveAspectRatio="none"
                style={{ display: "block", border: `1px solid ${B.border}`, borderRadius: 6, background: B.surfaceMuted }}>
                {(() => {
                  const vals = history.map(h => h.rate);
                  const min = Math.min(...vals);
                  const max = Math.max(...vals);
                  const range = max - min || 0.0001;
                  const pts = vals.map((v, i) => `${i},${80 - ((v - min) / range) * 70}`).join(" ");
                  return <polyline points={pts} fill="none" stroke={B.navy} strokeWidth="0.4" strokeLinejoin="round" />;
                })()}
              </svg>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: B.textLt, marginTop: 4 }}>
                <span>{fmt.date(history[0]?.valueDate)}</span>
                <span>{fmt.date(history[history.length - 1]?.valueDate)}</span>
              </div>
            </div>
          )}
          {!histLoading && history.length > 0 && (
            <Table
              columns={[
                { key: "valueDate", label: "Effective Date", render: v => fmt.date(v) },
                { key: "rate",      label: "Value",          render: v => <span style={{ fontWeight: 700, color: B.navy }}>{fmt.indexValue(v, historyIndex?.unit)}</span> },
                { key: "change",    label: "Change",
                  render: (_, row, i) => {
                    if (i === 0) return <span style={{ color: B.textLt }}>—</span>;
                    const prev = history[i - 1]?.rate || 0;
                    const level = isLevelUnit(historyIndex?.unit);
                    // A rate moves in percentage points; an index moves in points.
                    const delta = level
                      ? Math.abs(row.rate - prev).toFixed(2)
                      : `${Math.abs((row.rate - prev) * 100).toFixed(2)}%`;
                    const up = row.rate > prev;
                    // For a rate, up is bad (it costs more); for an index, up is good.
                    const color = level ? (up ? B.green : B.red) : (up ? B.red : B.green);
                    return <span style={{ color, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 3 }}><Icon name={up ? "trendUp" : "trendDown"} size={13} /> {delta}</span>;
                  }
                },
              ]}
              data={history.map((h, i) => ({ ...h, change: i }))}
            />
          )}
        </Modal>
      )}
    </div>
  );
};
